import type {
  AuthzCapabilities,
  OrgPermission,
  ProjectPermission,
} from "@/app/capabilities/types";

type PermissionChecker<P extends string> = (permission: P) => boolean;

const allowAll: PermissionChecker<string> = () => true;

// One checker per authz object identity, so a React Query `select` returns a
// stable function reference across renders.
const checkerCache = new WeakMap<
  AuthzCapabilities<string>,
  PermissionChecker<string>
>();

/**
 * Resolve a permission checker from an authz block.
 *
 * `enabled: false` is the explicit "RBAC is off" signal — there are no permissions
 * to enforce, so everything is allowed (matches the no-RBAC status quo). When RBAC
 * is on, it's strict membership: an unheld permission is denied.
 *
 * TODO(PLT-2150): once the backend returns the full permission set while RBAC is
 * off, drop the `enabled` short-circuit and make this strictly membership-based
 * (fail-closed) — i.e. always `return (p) => set.has(p)`, so a missing/empty set
 * denies. Blocked on that backend change (else the dashboard blanks out in prod).
 */
function permissionChecker<P extends string>(
  authz: AuthzCapabilities<P> | undefined
): PermissionChecker<P> {
  if (!authz?.enabled) {
    return allowAll;
  }

  const cached = checkerCache.get(authz);
  if (cached) {
    return cached;
  }

  const set = new Set<string>(authz.permissions);
  const checker: PermissionChecker<string> = (permission) =>
    set.has(permission);
  checkerCache.set(authz, checker);
  return checker;
}

export const hasProjectPermission = (
  authz: AuthzCapabilities<ProjectPermission> | undefined,
  permission: ProjectPermission
): boolean => permissionChecker(authz)(permission);

export const createProjectPermissionChecker = (
  authz: AuthzCapabilities<ProjectPermission> | undefined
): PermissionChecker<ProjectPermission> => permissionChecker(authz);

/** Stable React Query select — derives a cached checker from project capabilities. */
export const selectProjectCan = (data: {
  authz: AuthzCapabilities<ProjectPermission>;
}) => createProjectPermissionChecker(data.authz);

export const hasOrgPermission = (
  authz: AuthzCapabilities<OrgPermission> | undefined,
  permission: OrgPermission
): boolean => permissionChecker(authz)(permission);

export const createOrgPermissionChecker = (
  authz: AuthzCapabilities<OrgPermission> | undefined
): PermissionChecker<OrgPermission> => permissionChecker(authz);

/** Stable React Query select — derives a cached checker from org capabilities. */
export const selectOrgCan = (data: {
  authz: AuthzCapabilities<OrgPermission>;
}) => createOrgPermissionChecker(data.authz);
